import React, { Fragment, useState, useMemo } from 'react';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client/react';
import { Notify } from 'notiflix';

import { GET_ALL_EVENTS, GET_MY_EVENT_REGISTRATIONS } from 'helper/graphql/queries';
import { REGISTER_FOR_EVENT } from 'helper/graphql/mutations';
import useAuth from 'hooks/useAuth';

import EventCard from './EventCard';
import EventRegisterModal from './EventRegisterModal';

const STATIC_FILTERS = [
	{ value: 'all',      label: 'All Events' },
	{ value: 'upcoming', label: 'Upcoming' },
	{ value: 'past',     label: 'Past' },
];

const formatTypeLabel = (eventType) =>
	eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const EventsPage = () => {
	const { isAuthenticated } = useAuth();

	const [filter, setFilter] = useState('upcoming');
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [registeringGuid, setRegisteringGuid] = useState(null);

	const { data: eventsData, loading: eventsLoading, refetch: refetchEvents } = useQuery(
		GET_ALL_EVENTS,
		{ variables: { isActive: true } }
	);

	const { data: registrationsData, refetch: refetchRegistrations } = useQuery(
		GET_MY_EVENT_REGISTRATIONS,
		{ skip: !isAuthenticated }
	);

	const [registerForEvent] = useMutation(REGISTER_FOR_EVENT);

	const events = useMemo(
		() => eventsData?.allEvents?.edges?.map((e) => e.node) || [],
		[eventsData]
	);

	const registeredGuids = useMemo(() => {
		const regs = registrationsData?.myEventRegistrations?.edges?.map((e) => e.node) || [];
		return new Set(regs.map((r) => r.event?.guid));
	}, [registrationsData]);

	const filteredEvents = useMemo(() => {
		const now = new Date();
		return events.filter((ev) => {
			const session = new Date(ev.sessionDatetime);
			if (filter === 'upcoming') return session > now;
			if (filter === 'past')     return session <= now;
			if (filter === 'all')      return true;
			// any other value is a type filter
			return ev.eventType === filter;
		});
	}, [events, filter]);

	const upcomingCount = useMemo(
		() => events.filter((ev) => new Date(ev.sessionDatetime) > new Date()).length,
		[events]
	);

	// Build type filter tabs dynamically from whatever types the API returns
	const typeFilters = useMemo(() => {
		const seen = new Set();
		events.forEach((ev) => { if (ev.eventType) seen.add(ev.eventType); });
		return [...seen].map((t) => ({ value: t, label: formatTypeLabel(t) }));
	}, [events]);

	const allFilterOptions = [...STATIC_FILTERS, ...typeFilters];

	const handleRegisterClick = (event) => {
		// Modal handles auth inline — just open it regardless
		setSelectedEvent(event);
	};

	const handleConfirmRegister = async () => {
		if (!selectedEvent) return;
		setRegisteringGuid(selectedEvent.guid);
		try {
			await registerForEvent({ variables: { eventGuid: selectedEvent.guid } });
			Notify.success('Registered! Check your email for the join link.');
			setSelectedEvent(null);
			refetchEvents();
			refetchRegistrations();
		} catch (err) {
			Notify.failure(err?.message || 'Registration failed. Please try again.');
		} finally {
			setRegisteringGuid(null);
		}
	};

	return (
		<Fragment>
			{/* Page Hero */}
			<div className="events-hero py-5 pt-lg-12">
				<Container>
					<Row className="align-items-center">
						<Col lg={8}>
							<div className="d-flex align-items-center gap-2 mb-3">
								<Badge bg="" className="events-hero-badge">
									<i className="fe fe-radio me-1"></i>Live Events
								</Badge>
								{upcomingCount > 0 && (
									<Badge bg="" className="events-upcoming-badge">
										{upcomingCount} upcoming
									</Badge>
								)}
							</div>
							<h1 className="events-hero-title">
								Community Events &amp; Sessions
							</h1>
							<p className="events-hero-subtitle">
								Join our monthly X (Twitter) Spaces and Hopeline clinical case
								discussions. Engage with experts, and receive
								an e-certificate of attendance.
							</p>
						</Col>
						<Col lg={4} className="mt-4 mt-lg-0">
							<div className="events-hero-stats">
								<div className="hero-stat">
									<span className="hero-stat-value">{events.length}</span>
									<span className="hero-stat-label">Total Events</span>
								</div>
								<div className="hero-stat-divider" />
								<div className="hero-stat">
									<span className="hero-stat-value">{upcomingCount}</span>
									<span className="hero-stat-label">Upcoming</span>
								</div>
								<div className="hero-stat-divider" />
								<div className="hero-stat">
									<span className="hero-stat-value">
										{registeredGuids.size}
									</span>
									<span className="hero-stat-label">Registered</span>
								</div>
							</div>
						</Col>
					</Row>
				</Container>
			</div>

			{/* Main Content */}
			<Container className="py-5">
				{/* Filter Tabs */}
				<div className="events-filter-bar mb-4">
					{allFilterOptions.map((opt) => (
						<button
							key={opt.value}
							className={`events-filter-btn ${filter === opt.value ? 'active' : ''}`}
							onClick={() => setFilter(opt.value)}
						>
							{opt.label}
						</button>
					))}
				</div>

				{/* Loading skeleton */}
				{eventsLoading && (
					<Row className="g-4">
						{[1, 2, 3].map((i) => (
							<Col key={i} lg={4} md={6}>
								<div className="event-skeleton" />
							</Col>
						))}
					</Row>
				)}

				{/* Events grid */}
				{!eventsLoading && filteredEvents.length > 0 && (
					<Row className="g-4">
						{filteredEvents.map((event) => (
							<Col key={event.guid} lg={4} md={6}>
								<EventCard
									event={event}
									isRegistered={registeredGuids.has(event.guid)}
									onRegister={handleRegisterClick}
									registering={registeringGuid === event.guid}
								/>
							</Col>
						))}
					</Row>
				)}

				{/* Empty state */}
				{!eventsLoading && filteredEvents.length === 0 && (
					<div className="events-empty-state">
						<i className="fe fe-calendar" style={{ fontSize: '3rem', opacity: 0.25 }}></i>
						<h5 className="mt-3 mb-1">No events found</h5>
						<p className="text-muted mb-0">
							{filter === 'upcoming'
								? 'No upcoming events at the moment. Check back soon.'
								: 'No events match this filter.'}
						</p>
					</div>
				)}
			</Container>

			{/* Registration modal */}
			<EventRegisterModal
				show={!!selectedEvent}
				onHide={() => setSelectedEvent(null)}
				event={selectedEvent}
				onConfirm={handleConfirmRegister}
				loading={!!registeringGuid}
			/>

			<style>{`
				/* Hero */
				.events-hero {
					background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
					padding: 4rem 0 3rem;
					color: white;
				}
				.events-hero-badge {
					background: rgba(246,133,33,0.2) !important;
					color: #F68521 !important;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 5px 12px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.events-upcoming-badge {
					background: rgba(16,185,129,0.2) !important;
					color: #34d399 !important;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 5px 12px;
					border-radius: 8px;
				}
				.events-hero-title {
					font-size: 2.25rem;
					font-weight: 800;
					color: white;
					line-height: 1.25;
					margin-bottom: 1rem;
				}
				.events-hero-subtitle {
					font-size: 1rem;
					color: rgba(255,255,255,0.7);
					line-height: 1.7;
					margin: 0;
					max-width: 540px;
				}
				.events-hero-stats {
					background: rgba(255,255,255,0.08);
					border: 1px solid rgba(255,255,255,0.12);
					border-radius: 16px;
					padding: 1.5rem;
					display: flex;
					align-items: center;
					justify-content: space-around;
				}
				.hero-stat {
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 0.25rem;
				}
				.hero-stat-value {
					font-size: 2rem;
					font-weight: 800;
					color: white;
					line-height: 1;
				}
				.hero-stat-label {
					font-size: 0.75rem;
					color: rgba(255,255,255,0.55);
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.4px;
				}
				.hero-stat-divider {
					width: 1px;
					height: 40px;
					background: rgba(255,255,255,0.15);
				}

				/* Filter bar */
				.events-filter-bar {
					display: flex;
					flex-wrap: wrap;
					gap: 0.5rem;
				}
				.events-filter-btn {
					background: #f1f5f9;
					border: 2px solid transparent;
					color: #64748b;
					font-size: 0.875rem;
					font-weight: 600;
					padding: 0.5rem 1.125rem;
					border-radius: 10px;
					cursor: pointer;
					transition: all 0.2s ease;
				}
				.events-filter-btn:hover {
					background: white;
					border-color: #e2e8f0;
					color: #1e293b;
				}
				.events-filter-btn.active {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border-color: transparent;
					color: white;
					box-shadow: 0 3px 10px rgba(246,133,33,0.3);
				}

				/* Skeleton */
				.event-skeleton {
					height: 320px;
					border-radius: 16px;
					background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
					background-size: 200% 100%;
					animation: skeleton-shimmer 1.4s infinite;
				}
				@keyframes skeleton-shimmer {
					0% { background-position: 200% 0; }
					100% { background-position: -200% 0; }
				}

				/* Empty state */
				.events-empty-state {
					text-align: center;
					padding: 4rem 2rem;
					color: #94a3b8;
				}

				/* Responsive */
				@media (max-width: 768px) {
					.events-hero { padding: 3rem 0 2rem; }
					.events-hero-title { font-size: 1.75rem; }
					.events-hero-stats { margin-top: 1.5rem; }
					.hero-stat-value { font-size: 1.5rem; }
				}
			`}</style>
		</Fragment>
	);
};

export default EventsPage;
